import type { ValXmlFieldAssignment } from '../types'
import { FORCED_DEFAULT_VALXML_VALUES, ROOT_DATE_VALXML_PATH } from '../constants'
import type { ValXmlAttributeDefinition } from '../valxml-schema'
import { VALXML_ATTRIBUTE_DEFINITIONS } from '../valxml-schema'

export const normaliseValXmlPathForGrouping = (input?: string | null): string | undefined => {
    if (!input) {
        return undefined
    }
    return input.replace(/^ValXML\./i, '').replace(/(^|[.])ns1:/gi, '$1')
}

export const canonicalizeValXmlPath = (value?: string | null): string | undefined => {
    if (!value) {
        return undefined
    }
    const trimmed = value.trim()
    if (!trimmed) {
        return undefined
    }
    const normalizedSeparators = trimmed.replace(/[\\/]+/g, '.').replace(/\s+/g, '')
    const withoutDuplicates = normalizedSeparators.replace(/\.+/g, '.').replace(/^\.+/, '').replace(/\.+$/, '')
    if (!withoutDuplicates) {
        return undefined
    }
    const withoutNamespace = withoutDuplicates.replace(/(^|[.])ns1:/gi, '$1')
    const withPrefix = withoutNamespace.match(/^valxml\./i) ? withoutNamespace : `ValXML.${withoutNamespace}`
    return withPrefix.replace(/^valxml/i, 'ValXML')
}

export const normalizePathForComparison = (value?: string | null): string | undefined => {
    const canonical = canonicalizeValXmlPath(value)
    if (!canonical) {
        return undefined
    }
    return canonical.toLowerCase()
}

export const normalizeNameForComparison = (value?: string | null): string | undefined => {
    if (!value) {
        return undefined
    }
    const trimmed = value.trim()
    if (!trimmed) {
        return undefined
    }
    return trimmed
        .toLowerCase()
        .replace(/[\s_/:-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[.]+$/g, '')
        .trim()
}

type XmlElementNode = {
    name: string
    attributes: Record<string, string>
    children: XmlElementNode[]
    text?: string
}

type BuildResult = {
    xml: string | null
    warnings: string[]
}

const ROOT_NAMESPACE = 'https://www.biis.de/fileadmin/BIISValXML/Schema/ValXML1.3'

const CHILD_ORDER: Record<string, string[]> = {
    'ns1:ValXML': ['ns1:Date', 'ns1:CompletionDate', 'ns1:PublicationDate', 'ns1:DataSupplier', 'ns1:Draft', 'ns1:BIISValuationData'],
    'ns1:DataSupplier': ['ns1:Short', 'ns1:Long', 'ns1:TypeOfDataSupplier'],
    'ns1:BIISValuationData': ['ns1:General', 'ns1:ValuationResults', 'ns1:ValuationResultsRentDataDetails'],
    'ns1:General': [
        'ns1:ArealUnit',
        'ns1:Address',
        'ns1:Owner',
        'ns1:PortfolioName',
        'ns1:ObjNoOwner',
        'ns1:ObjNoOwnerTechnical',
        'ns1:ObjKoWGS84Longitude',
        'ns1:ObjKoWGS84Latitude',
        'ns1:ObjPicture',
    ],
    'ns1:Address': ['ns1:Street', 'ns1:HouseNumber', 'ns1:PostCode', 'ns1:Town', 'ns1:Country', 'ns1:Text'],
    'ns1:ValuationResults': [
        'ns1:MainValuerName',
        'ns1:MainValuerID',
        'ns1:MainValuerDateOfInspection',
        'ns1:CoValuer1Name',
        'ns1:CoValuer1ID',
        'ns1:CoValuer1DateOfInspection',
        'ns1:CoValuer2Name',
        'ns1:CoValuer2ID',
        'ns1:CoValuer2DateOfInspection',
        'ns1:RebaseType1',
        'ns1:RebaseType2',
        'ns1:AdditionalClientSpecificInformation',
        'ns1:RebaseObjAdditionalInformation',
        'ns1:DateOfAppraisal',
        'ns1:QualityDateOfAppraisal',
        'ns1:QualityDateOfAppraisalAsDate',
        'ns1:Currency',
        'ns1:ExchangeRate1EUR',
        'ns1:DateExchangeRate',
        'ns1:MainTypeOfUse',
        'ns1:ShareMainTypeOfUse',
        'ns1:AncillaryTypeOfUse',
        'ns1:ShareAncillaryTypeOfUse',
        'ns1:TypeOfOwnership',
        'ns1:SingleTenant',
        'ns1:PurchasePrice',
        'ns1:DateOfPurchase',
        'ns1:PriceOfSale',
        'ns1:DateOfSale',
        'ns1:LocationQuality',
        'ns1:StructuralCondition',
        'ns1:FitOutQuality',
        'ns1:StateOfCompletion',
        'ns1:MaintenanceBacklog',
        'ns1:Floors',
        'ns1:NormalTotalEconomicLife',
        'ns1:RemainingEconomicLife',
        'ns1:OriginalYearOfConstruction',
        'ns1:OriginalYearOfConstructionAsYearNumber',
        'ns1:YearOfLatestMajorRenovation',
        'ns1:CalculatedYearOfConstruction',
        'ns1:DateOfChangeForRemainingEconomicLife',
        'ns1:LandSize',
        'ns1:FloorToAreaRatio',
        'ns1:SiteCoverageRatio',
        'ns1:GrossFloorSpaceOverground',
        'ns1:GrossFloorSpaceBelowGround',
        'ns1:TotalGrossFloorSpace',
        'ns1:NetFloorSpace',
        'ns1:VacantArea',
        'ns1:MarketValue',
        'ns1:MarketValueDate',
        'ns1:MarketValuePreviousYear',
        'ns1:MarketValuePreviousYearDate',
        'ns1:MarketValueChangeReason',
        'ns1:MarketValueChangeAmount',
        'ns1:MarketValueChangeRatio',
    ],
}

const createBaseDocument = (): XmlElementNode => ({
    name: 'ns1:ValXML',
    attributes: {
        xmlns: ROOT_NAMESPACE,
        'xmlns:xs': 'http://www.w3.org/2001/XMLSchema',
        'xmlns:xsi': 'http://www.w3.org/2001/XMLSchema-instance',
        'xmlns:ns1': ROOT_NAMESPACE,
        'xsi:schemaLocation': `${ROOT_NAMESPACE} ${ROOT_NAMESPACE}.xsd`,
    },
    children: [],
})

const createElement = (name: string): XmlElementNode => ({
    name,
    attributes: {},
    children: [],
})

const ATTRIBUTE_DEFINITION_MAP = new Map<string, ValXmlAttributeDefinition>()
VALXML_ATTRIBUTE_DEFINITIONS.forEach((definition) => {
    const canonical = canonicalizeValXmlPath(definition.path)
    if (canonical) {
        ATTRIBUTE_DEFINITION_MAP.set(canonical, definition)
    }
})

const insertChild = (parent: XmlElementNode, child: XmlElementNode) => {
    const order = CHILD_ORDER[parent.name]
    if (!order || order.length === 0) {
        parent.children.push(child)
        return
    }

    const desiredIndex = order.indexOf(child.name)
    if (desiredIndex === -1) {
        parent.children.push(child)
        return
    }

    let insertAt = parent.children.length
    for (let index = 0; index < parent.children.length; index += 1) {
        const sibling = parent.children[index]
        const siblingOrder = order.indexOf(sibling.name)
        if (siblingOrder === -1) {
            continue
        }
        if (siblingOrder > desiredIndex) {
            insertAt = index
            break
        }
    }

    parent.children.splice(insertAt, 0, child)
}

const ensureChild = (parent: XmlElementNode, rawSegment: string): XmlElementNode => {
    const { name } = parseSegment(rawSegment)
    const elementName = name === 'ValXML' ? 'ns1:ValXML' : `ns1:${name}`

    let child = parent.children.find((existing) => existing.name === elementName)
    if (!child) {
        child = createElement(elementName)
        insertChild(parent, child)
    }
    return child
}

const parseSegment = (segment: string): { name: string } => {
    const match = segment.match(/^([^[\]()@]+)(?:[[(].*[\])])?$/)
    const cleaned = match?.[1] ?? segment
    return { name: cleaned }
}

const applyAssignment = (root: XmlElementNode, assignment: ValXmlFieldAssignment, warnings: string[]) => {
    const canonicalPath = canonicalizeValXmlPath(assignment.path)
    if (!canonicalPath) {
        if (assignment.path) {
            warnings.push(`Pfad "${assignment.path}" ist ungültig und wird ignoriert.`)
        }
        return
    }

    const segments = canonicalPath.split('.').filter((segment) => segment.length > 0)
    if (segments.length === 0) {
        return
    }

    const firstSegment = segments[0]
    if (firstSegment.toLowerCase() !== 'valxml') {
        warnings.push(`Pfad "${canonicalPath}" beginnt nicht mit "ValXML" und wird ignoriert.`)
        return
    }

    if (segments.length === 1) {
        warnings.push(`Pfad "${canonicalPath}" verweist auf das Root-Element und wird ignoriert.`)
        return
    }

    const nextValue = assignment.value
    let finalValue: string | null = null
    if (nextValue && nextValue.trim().length > 0) {
        const { value, warning } = normalizeAssignmentValue(canonicalPath, nextValue)
        if (warning) {
            warnings.push(`Pfad "${canonicalPath}": ${warning}`)
        }
        if (value === null) {
            return
        }
        finalValue = value
    }

    let current = root
    for (let index = 1; index < segments.length; index += 1) {
        const segment = segments[index]
        current = ensureChild(current, segment)
    }

    if (finalValue === null) {
        if (current.text && current.text.trim().length > 0) {
            warnings.push(`Pfad "${assignment.path}" wurde bereits mit "${current.text}" gesetzt und kann nicht auf nil geändert werden.`)
            return
        }
        if (current.attributes['xsi:nil'] !== 'true') {
            current.attributes['xsi:nil'] = 'true'
        }
        delete current.text
        return
    }

    if (current.text === finalValue) {
        return
    }

    if (current.text && current.text !== finalValue) {
        warnings.push(`Pfad "${canonicalPath}" wird überschrieben. Alter Wert: "${current.text}", neuer Wert: "${finalValue}".`)
    }
    delete current.attributes['xsi:nil']
    current.text = finalValue
}

const escapeXml = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;')

const serializeElement = (node: XmlElementNode, indentLevel = 0): string => {
    const indent = '  '.repeat(indentLevel)
    const attributes = Object.entries(node.attributes)
        .map(([key, value]) => `${key}="${escapeXml(value)}"`)
        .join(' ')
    const openingTag = attributes.length > 0 ? `<${node.name} ${attributes}>` : `<${node.name}>`

    if (
        (node.children.length === 0 || node.name === 'ns1:ValXML') &&
        (node.text === undefined || node.text === null || node.text.length === 0)
    ) {
        if (node.children.length === 0) {
            return `${indent}${openingTag.replace(/>$/, ' />')}`
        }
    }

    if (node.text && node.children.length === 0) {
        return `${indent}${openingTag}${escapeXml(node.text)}</${node.name}>`
    }

    const childXml = node.children
        .map((child) => serializeElement(child, indentLevel + 1))
        .filter((line) => line.trim().length > 0)
        .join('\n')

    if (childXml.length === 0 && (!node.text || node.text.length === 0)) {
        return `${indent}${openingTag.replace(/>$/, ' />')}`
    }

    const closingTag = `</${node.name}>`
    if (childXml.length > 0 && node.text && node.text.trim().length > 0) {
        return `${indent}${openingTag}${escapeXml(node.text)}${childXml}${closingTag}`
    }
    if (childXml.length > 0) {
        return node.text && node.text.trim().length > 0
            ? `${indent}${openingTag}${escapeXml(node.text)}${childXml}${closingTag}`
            : `${indent}${openingTag}\n${childXml}\n${indent}${closingTag}`
    }

    return `${indent}${openingTag}${escapeXml(node.text ?? '')}${closingTag}`
}

const DEFAULT_FIELD_ASSIGNMENTS: ValXmlFieldAssignment[] = Object.entries(FORCED_DEFAULT_VALXML_VALUES).map(([path, value]) => ({
    path,
    value,
}))

type NormalizedValueResult = {
    value: string | null
    warning?: string
}

const DECIMAL_BASE_TYPES = new Set(['xs:decimal', 'xs:double', 'xs:float'])
const DATE_BASE_TYPES = new Set(['xs:date'])
const INTEGER_BASE_TYPES = new Set([
    'xs:int',
    'xs:integer',
    'xs:long',
    'xs:short',
    'xs:byte',
    'xs:nonnegativeinteger',
    'xs:nonNegativeInteger',
    'xs:positiveinteger',
    'xs:positiveInteger',
    'xs:unsignedint',
    'xs:unsignedInt',
    'xs:unsignedlong',
    'xs:unsignedLong',
    'xs:unsignedshort',
    'xs:unsignedShort',
    'xs:unsignedbyte',
    'xs:unsignedByte',
])

const normalizeAssignmentValue = (path: string, rawValue: string): NormalizedValueResult => {
    if (path === ROOT_DATE_VALXML_PATH) {
        return normalizeDateAssignmentValue(rawValue)
    }

    const definition = ATTRIBUTE_DEFINITION_MAP.get(path)
    if (!definition) {
        return { value: rawValue }
    }

    const baseType = (definition.baseType ?? definition.simpleType?.base ?? '').trim()
    if (!baseType) {
        return { value: rawValue }
    }

    if (DECIMAL_BASE_TYPES.has(baseType)) {
        const normalized = normalizeDecimalString(rawValue)
        if (!normalized) {
            return {
                value: null,
                warning:
                    `erwartet Dezimalwert, konnte "${rawValue}" nicht interpretieren. ` +
                    'Feld wird nicht exportiert; bitte Wert korrigieren.',
            }
        }
        return { value: normalized }
    }

    if (DATE_BASE_TYPES.has(baseType)) {
        return normalizeDateAssignmentValue(rawValue)
    }

    if (INTEGER_BASE_TYPES.has(baseType) || baseType.toLowerCase().endsWith('integer')) {
        const normalized = normalizeDecimalString(rawValue)
        if (!normalized) {
            return {
                value: null,
                warning:
                    `erwartet Ganzzahlwert, konnte "${rawValue}" nicht interpretieren. ` +
                    'Feld wird nicht exportiert; bitte Wert korrigieren.',
            }
        }
        if (normalized.includes('.')) {
            const trimmed = normalized.replace(/\.0+$/, '')
            if (trimmed.includes('.')) {
                return {
                    value: null,
                    warning:
                        `erwartet Ganzzahlwert, erhielt Dezimalwert "${rawValue}". ` +
                        'Feld wird nicht exportiert; bitte Wert korrigieren.',
                }
            }
            return { value: trimmed || '0' }
        }
        return { value: normalized }
    }

    return { value: rawValue }
}

const normalizeDateAssignmentValue = (rawValue: string): NormalizedValueResult => {
    const normalized = normalizeDateString(rawValue)
    if (!normalized) {
        return {
            value: null,
            warning:
                `erwartet Datum im Format YYYY-MM-DD, konnte "${rawValue}" nicht interpretieren. ` +
                'Feld wird nicht exportiert; bitte Wert korrigieren.',
        }
    }
    return { value: normalized }
}

const normalizeDateString = (input: string): string | null => {
    const trimmed = input.trim()
    if (!trimmed) {
        return null
    }

    const isoDate = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:T.*)?$/)
    if (isoDate) {
        return toValidIsoDate(isoDate[1], isoDate[2], isoDate[3])
    }

    const yearFirstDate = trimmed.match(/^(\d{4})[./](\d{1,2})[./](\d{1,2})$/)
    if (yearFirstDate) {
        return toValidIsoDate(yearFirstDate[1], yearFirstDate[2], yearFirstDate[3])
    }

    const dayFirstDate = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/)
    if (dayFirstDate) {
        return toValidIsoDate(normalizeYear(dayFirstDate[3]), dayFirstDate[2], dayFirstDate[1])
    }

    return null
}

const normalizeYear = (year: string): string => {
    if (year.length === 4) {
        return year
    }

    const numericYear = Number.parseInt(year, 10)
    return numericYear >= 70 ? `19${year}` : `20${year}`
}

const toValidIsoDate = (year: string, month: string, day: string): string | null => {
    const numericYear = Number.parseInt(year, 10)
    const numericMonth = Number.parseInt(month, 10)
    const numericDay = Number.parseInt(day, 10)

    if (!Number.isInteger(numericYear) || !Number.isInteger(numericMonth) || !Number.isInteger(numericDay)) {
        return null
    }

    const date = new Date(Date.UTC(numericYear, numericMonth - 1, numericDay))
    if (date.getUTCFullYear() !== numericYear || date.getUTCMonth() !== numericMonth - 1 || date.getUTCDate() !== numericDay) {
        return null
    }

    return `${String(numericYear).padStart(4, '0')}-${String(numericMonth).padStart(2, '0')}-${String(numericDay).padStart(2, '0')}`
}

const normalizeDecimalString = (input: string): string | null => {
    if (!input) {
        return null
    }
    const trimmed = input.trim()
    if (!trimmed) {
        return null
    }

    let working = trimmed.replace(/\b(EUR)\b/gi, '')

    working = working.replace(/\u2212/g, '-').replace(/\s+/g, '')

    let sign = ''
    if (working.startsWith('+') || working.startsWith('-')) {
        sign = working[0] === '-' ? '-' : ''
        working = working.slice(1)
    }

    const hadCommaDash = /,-+$/.test(working)
    if (hadCommaDash) {
        working = working.replace(/,-+$/g, '')
    }

    working = working.replace(/[^0-9,.-]/g, '')
    if (!working || !/\d/.test(working)) {
        return null
    }

    const dotCount = (working.match(/\./g) ?? []).length
    const commaCount = (working.match(/,/g) ?? []).length

    let numeric = working

    if (commaCount > 0 && dotCount > 0) {
        const lastComma = working.lastIndexOf(',')
        const lastDot = working.lastIndexOf('.')
        if (lastComma > lastDot) {
            numeric = numeric.replace(/\./g, '')
            numeric = numeric.replace(',', '.')
        } else {
            numeric = numeric.replace(/,/g, '')
        }
    } else if (commaCount > 0) {
        numeric = numeric.replace(/\./g, '')
        numeric = numeric.replace(',', '.')
    } else if (dotCount > 1) {
        numeric = numeric.replace(/\./g, '')
    } else if (dotCount === 1) {
        numeric = numeric.replace(/,/g, '')
    } else {
        numeric = numeric.replace(/[.,]/g, '')
    }

    if (!numeric || !/^\d+(\.\d+)?$/.test(numeric)) {
        return null
    }

    if (hadCommaDash && !numeric.includes('.')) {
        numeric = `${numeric}.00`
    } else if (numeric.includes('.')) {
        numeric = numeric.replace(/0+$/, '').replace(/\.$/, '')
    }

    const result = sign ? `${sign}${numeric}` : numeric
    return result.startsWith('+') ? result.slice(1) : result
}

export const buildValXml = (assignments: ValXmlFieldAssignment[]): BuildResult => {
    const warnings: string[] = []
    const meaningfulAssignments = assignments.filter((assignment) => assignment.path?.length)

    if (meaningfulAssignments.length === 0) {
        warnings.push('Keine ValXML-Felder vorhanden – es wird kein ValXML erstellt.')
        return { xml: null, warnings }
    }

    const root = createBaseDocument()
    applyAssignment(root, { path: ROOT_DATE_VALXML_PATH, value: getCurrentIsoDate() }, warnings)
    meaningfulAssignments.forEach((assignment) => {
        applyAssignment(root, assignment, warnings)
    })
    DEFAULT_FIELD_ASSIGNMENTS.forEach((assignment) => {
        applyAssignment(root, assignment, warnings)
    })

    const xmlBody = serializeElement(root)
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n${xmlBody}`
    return { xml, warnings }
}

const getCurrentIsoDate = (): string => new Date().toISOString().slice(0, 10)

export const deriveValXmlFilename = (originalName?: string | null): string => {
    const fallback = 'valuation-export'
    if (!originalName) {
        return `${fallback}.xml`
    }

    const baseName = originalName.replace(/\.[^./\\]+$/u, '').trim()
    const safeBase = (baseName.length > 0 ? baseName : fallback).replace(/[^a-zA-Z0-9._-]+/g, '_')
    return `${safeBase}.xml`
}
